import { queryClient } from '@/lib/react-query'

import { userKeys } from '@/shared/auth/queries'

import { ROLES } from './constants'
import { tenantSessionKeys } from './tenant-session'
import { getCurrentTenant } from './tenant-store'

import type { Role, TenantDetails, UserRequest } from './types'

/**
 * @returns current role for active tenant — reads from React Query cache
 * Safe in beforeLoad and other non-React code after _dashboard beforeLoad runs.
 */
export const getCurrentRole = (): Role | undefined => {
	const tenant = getCurrentTenant()
	const meData = queryClient.getQueryData<UserRequest>(userKeys.me())
	return tenant && meData
		? (meData.tenant_roles[tenant] as Role | undefined)
		: undefined
}

/**
 * @returns current SKU for active tenant — reads from React Query cache
 */
export const getCurrentSKU = () => {
	const tenant = getCurrentTenant()
	if (!tenant) return undefined
	return queryClient.getQueryData<TenantDetails>(
		tenantSessionKeys.details(tenant),
	)?.sku_info
}

/**
 * @returns feature flags for active tenant — reads from React Query cache.
 * Safe in beforeLoad and other non-React code after _dashboard beforeLoad runs.
 * Treat absence as "all flags off" (fail closed).
 */
export const getTenantFeatures = () => {
	const tenant = getCurrentTenant()
	if (!tenant) return undefined
	return queryClient.getQueryData<TenantDetails>(
		tenantSessionKeys.details(tenant),
	)?.features
}

/**
 * @returns whether active tenant is the user's owning tenant
 */
export const getIsOwnerTenant = (): boolean => {
	const tenant = getCurrentTenant()
	const meData = queryClient.getQueryData<UserRequest>(userKeys.me())
	return meData?.auth_context?.owning_tenant === tenant
}

/**
 * @returns IDs of tenants the current user is Admin on — reads from React Query cache.
 * Falls back to [getCurrentTenant()] if cache is empty.
 * Use in API functions that hit tenant="all" endpoints to scope the X-AIR-Request-Tenants header.
 */
export const getAdminTenantIds = (): string[] => {
	const meData = queryClient.getQueryData<UserRequest>(userKeys.me())
	const fallback = getCurrentTenant()
	if (!meData) return fallback ? [fallback] : []
	return meData.allowed_tenants
		.filter((t) => meData.tenant_roles[t.id] === ROLES.Admin)
		.map((t) => t.id)
}
